import { CapabilityIdentifier } from '@/features/billing/capability-identifiers';
import { useFeatureLock } from '@/features/billing/use-feature-lock';
import { useSubscriptionPaused } from '@/features/billing/use-subscription-paused';

export type CreateApiKeyGate = {
  /** Neither the unlocked UI nor a gated reason should render yet. */
  isResolving: boolean;
  /**
   * Non-null when the control must render disabled with this reason — a
   * paused subscription. Role is NOT here: a role-blocked control is hidden
   * (`RoleGate` / the palette's role filter), never disabled.
   */
  blockedReason: string | null;
  /** The org's plan doesn't grant `api-keys/create` (and it's not already
   * blocked by pause — that reason takes precedence). */
  planLocked: boolean;
  /** Copy for the plan-locked case, e.g. "Unlocks on the Growth plan." */
  planLockedReason: string | null;
};

/**
 * The exact paused/plan gating `ApiKeysActions` applies to the real
 * "Create API key" button, extracted so the command palette's "Create API
 * key" quick action can render the identical disabled-with-reason state
 * instead of re-deriving its own copy of this ladder.
 */
export function useCreateApiKeyGate(): CreateApiKeyGate {
  const { reason: pausedReason } = useSubscriptionPaused();
  const lock = useFeatureLock(CapabilityIdentifier.API_KEYS_CREATE);

  const blockedReason = pausedReason;
  const planLocked = blockedReason === null && lock.isLocked;
  const planLockedReason = lock.unlockingPlanName
    ? `Unlocks on the ${lock.unlockingPlanName} plan.`
    : lock.preview;

  return {
    isResolving: blockedReason === null && lock.isResolving,
    blockedReason,
    planLocked,
    planLockedReason: planLocked ? planLockedReason : null,
  };
}
